/**
 * The design tree, banded by containment depth.
 *
 * Read-only, and that is not an omission. A requirement's parent is a FIELD —
 * `:>> parent = MO_1` — so it can be PATCHed. A part's place in the
 * decomposition is where its declaration physically sits in the file, and
 * moving it means moving the declaration; there is no `parent` to write, and no
 * move route (contract §8 leaves that out of this pass deliberately). Offering a
 * drag here would be a promise the API cannot keep, so the canvas is told there
 * is no re-parent to offer and says why in its own hint line.
 *
 * /api/design already answers depth-first rows carrying `depth`, and it steps
 * THROUGH a usage's definition — `part gnc : Guidance` expands into Guidance's
 * own parts — so a definition's children legitimately appear once per usage of
 * it. That is why `designNodes` keys on the row's position rather than on the
 * qualified name.
 *
 * Folds the way the requirements hierarchy does (issue #93): by depth from the
 * quick buttons, and box by box from each box's +N. A large design opens folded
 * to its second band - the parts directly inside each root.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { api } from '../../api'
import { findKind } from '../../schema'
import type { DesignRow } from '../../schema'
import { useSchema } from '../../state/AppData'
import EmptyState from '../common/EmptyState'
import DiagramCanvas from './DiagramCanvas'
import FoldBar from './FoldBar'
import Legend from './Legend'
import { defaultCutoff, fold, toggle } from './focus'
import type { Folding } from './focus'
import { designBandLabels, designNodes, layout } from './layout'
import { useDiagramRows } from './useDiagramRows'

export default function PartsHierarchy() {
  const { rows, error } = useDiagramRows<DesignRow>(api.design)
  const schema = useSchema()
  const navigate = useNavigate()

  const plural = findKind(schema, 'part')?.plural ?? 'parts'

  const nodes = useMemo(() => (rows ? designNodes(rows) : []), [rows])
  const labels = useMemo(() => designBandLabels(nodes), [nodes])
  const [chosenFolding, setFolding] = useState<Folding | null>(null)
  const folding = useMemo<Folding>(
    () => chosenFolding ?? { cutoff: defaultCutoff(nodes), open: new Map() },
    [chosenFolding, nodes],
  )
  const folded = useMemo(() => fold(nodes, folding), [nodes, folding])
  const diagram = useMemo(() => layout(folded.nodes, labels), [folded, labels])
  const bands = useMemo(
    () =>
      [...new Set(nodes.map((node) => node.band))]
        .sort((a, b) => a - b)
        .map((band) => ({ band, label: labels[band] ?? String(band) })),
    [nodes, labels],
  )
  const roots = nodes.filter((node) => node.band === 0).length

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!rows) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }
  if (nodes.length === 0) {
    return (
      <EmptyState
        title="There is no system design to draw"
        description="This diagram is generated from GET /api/design. Declare a part usage — a part def on its own is a declaration, not decomposition, so it is not a root here."
        actions={[{ label: 'Go to parts', onClick: () => navigate(`/${plural}`) }]}
      />
    )
  }

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        The system decomposition, banded by containment depth. Generated from{' '}
        <code>GET /api/design</code>, which follows usages and steps through each usage’s
        definition, so a definition’s parts are drawn under every usage of it.
        {roots > 1 &&
          ` ${roots} parts sit at the top level: anything not contained by another part is a root,` +
            ' which is how stakeholders and ground elements appear beside the vehicle.'}
      </Typography>

      <FoldBar
        bands={bands}
        folding={folding}
        onChange={setFolding}
        shown={folded.nodes.length}
        total={nodes.length}
        noun="parts"
      />

      <Legend
        items={[
          { tone: 'primary', label: 'Part usage' },
          { tone: 'secondary', label: 'Part definition' },
        ]}
      />

      <DiagramCanvas
        diagram={diagram}
        emptyLabel="Nothing to draw."
        folds={folded.folds}
        onToggle={(key, state) => setFolding(toggle(folding, key, state))}
        onOpen={(node) => {
          if (node.id) navigate(`/${plural}/${encodeURIComponent(node.id)}`)
        }}
        reparent={null}
      />
    </Stack>
  )
}
